import {
  EJoinType,
  IDatabaseColumnModel,
  IDatabaseTableModel,
  IDbJoinDataDto,
  IGenericSelectDataModel,
  IGenericSelectTablesModel
} from "@kuika/kuika-cl-model";
import { FunctionComponent, useEffect, useState } from "react";
import ReactFlow, {
  applyNodeChanges,
  Edge,
  Node,
  NodeChange,
  NodeTypes,
  Panel,
  Position,
  ReactFlowInstance,
  useReactFlow
} from "reactflow";
import "reactflow/dist/style.css";
import { DatasourceService } from "../../../../services/datasource-service";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSpinnerSmall from "../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallBoldText from "../../../ui-kit/typography/SmallBoldText/SmallBoldText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import "./add-join-modal.scss";
import CustomNode, { FkPkHandle } from "./generic-sql-components/custom-node";

const nodeWidth = 190;
const nodeHeight = 184;
const nodeWidthGap = 30;
const nodeHeightGap = 60;

// Define nodeTypes outside the component to prevent re-creation on every render
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nodeTypes = {
  Origin: CustomNode,
  Parent: CustomNode,
  Child: CustomNode
} as NodeTypes;

interface Props {
  tables: IDatabaseTableModel[];
  tableName: string;
  tableSchema: string;
  closeModal: () => void;
  handleAddJoins: (data: IGenericSelectDataModel, treeType: ETreeType) => void;
  selectDataTables: IGenericSelectTablesModel[];
  selectDataRelations: { fromFk: string; fromTable: string; toTable: string; toPk: string }[];
}

type Rotation = "vertical" | "horizontal";

// New node structure: Each node represents a specific FK relationship
interface FkNode {
  nodeId: string;
  tableId: string;
  tableName: string;
  tableSchema: string;
  type: ETreeType;
  nodeBackColor: string;
  // FK relationship info
  fk: string; // FK column name
  pk: string; // PK column name
  // For parent nodes: this is the master table connected via child's FK
  // For child nodes: this is the detail table that has the FK
  parentNodeId?: string; // Node that this connects to (towards origin)
  // Master/Detail info
  masterTable: string;
  masterTableSchema: string;
  masterTableId: string;
  detailTable: string;
  detailTableSchema: string;
  detailTableId: string;
  // Children nodes (nodes that are further from origin through this node)
  children: FkNode[];
  // For tree traversal
  level: number;
}

export enum ETreeType {
  Origin = "Origin",
  Parent = "Parent",
  Child = "Child"
}

const selectedColor = "var(--blue-4)";
const parentColor = "var(--purple-4)";
const childColor = "var(--orange-3)";

interface AddJoinWarningPanel {
  isOpen: boolean;
  message?: string;
}

interface PathSelection {
  selectedPath: Node[]; // Ordered list of nodes forming the path
}

const zoomOptions = [
  { value: 0.25, label: "25%" },
  { value: 0.5, label: "50%" },
  { value: 0.75, label: "75%" },
  { value: 1, label: "100%" },
  { value: 1.25, label: "125%" },
  { value: 1.5, label: "150%" },
  { value: 2, label: "200%" }
];

interface ZoomControlsProps {
  joinDataDto: IDbJoinDataDto[];
  rotation: Rotation;
  setRotation: (rotation: Rotation) => void;
}

const ZoomControls: React.FC<ZoomControlsProps> = ({ joinDataDto, rotation, setRotation }) => {
  const { zoomIn, zoomOut, fitView, getZoom, setViewport, getViewport } = useReactFlow();
  const [currentZoom, setCurrentZoom] = useState(1);

  useEffect(() => {
    const zoom = getZoom();
    setCurrentZoom(zoom);
  }, [getZoom]);

  const handleZoomIn = () => {
    zoomIn();
    setTimeout(() => setCurrentZoom(getZoom()), 100);
  };

  const handleZoomOut = () => {
    zoomOut();
    setTimeout(() => setCurrentZoom(getZoom()), 100);
  };

  const handleResetZoom = () => {
    fitView();
    setTimeout(() => setCurrentZoom(getZoom()), 100);
  };

  const handleZoomChange = (value: number) => {
    const viewport = getViewport();
    setViewport({ ...viewport, zoom: value });
    setCurrentZoom(value);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <div
        style={{
          backgroundColor: "var(--dark-0)",
          padding: "4px 2px",
          borderRadius: "var(--radius-m)",
          display: "flex",
          alignItems: "center",
          gap: "2px"
        }}
      >
        <KuikaButton color="neutral" type="tertiary" size="sm" isIcon onClick={handleZoomIn}>
          <KuikaSvg name="plus" width={16} height={16} />
        </KuikaButton>
        <KuikaButton color="neutral" type="tertiary" size="sm" isIcon onClick={handleZoomOut}>
          <KuikaSvg name="minus" width={16} height={16} />
        </KuikaButton>
        <KuikaButton color="neutral" type="tertiary" size="sm" isIcon onClick={handleResetZoom}>
          <KuikaSvg name="photo-sensor-3" width={16} height={16} />
        </KuikaButton>
      </div>
      <KuikaSelectbox
        size="md"
        value={zoomOptions.find((opt) => Math.abs(opt.value - currentZoom) < 0.1)?.value ?? 1}
        options={zoomOptions}
        onChange={(value) => handleZoomChange(value as number)}
        className="add-join-modal__zoom-select"
      />
    </div>
  );
};

export const AddJoinModal: FunctionComponent<Props> = (props) => {
  const [pathSelection, setPathSelection] = useState<PathSelection>({ selectedPath: [] });
  const [nodes, setNodes] = useState([] as Node[]);
  const [edges, setEdges] = useState([] as Edge[]);
  const [rotation, setRotation] = useState("vertical" as Rotation);
  const [joinDataDto, setJoinDataDto] = useState([] as IDbJoinDataDto[]);
  const [reactflowInstance, setReactflowInstance] = useState({} as ReactFlowInstance);
  const [isLoading, setIsLoading] = useState(true);
  const [warningPanel, setWarningPanel] = useState({
    isOpen: false
  } as AddJoinWarningPanel);

  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    fetchDbJoinData();
  }, []);

  useEffect(() => {
    processData(joinDataDto);
  }, [rotation]);

  useEffect(() => {
    reactflowInstance?.fitView && reactflowInstance.fitView();
  }, [rotation, isMaximized]);

  useEffect(() => {
    if (nodes.length === 0) return;
    if (nodes.length < 2) {
      setWarningPanel({
        isOpen: true,
        message: "There is no database table that you can join."
      });
    } else {
      // Check if there are any FK relationships not yet in relations
      let hasNewRelation = false;
      const existingRelations = props.selectDataRelations || [];

      nodes.forEach((node) => {
        if (node.data.type === ETreeType.Origin) return;
        const nodeData = node.data;
        // Check if this FK->PK relationship already exists
        const relationExists = existingRelations.some((rel) => rel.fromFk === nodeData.fk && rel.toPk === nodeData.pk);
        if (!relationExists) {
          hasNewRelation = true;
        }
      });

      if (hasNewRelation) {
        setWarningPanel({ isOpen: false });
      } else {
        setWarningPanel({
          isOpen: true,
          message: "All joins already included in your select action relations."
        });
      }
    }
  }, [nodes, props.selectDataRelations]);

  const fetchDbJoinData = async () => {
    try {
      const res = await DatasourceService.GetJoinData(props.tableName, props.tableSchema);
      if (res && res.status === 200) {
        const data = res.data;
        setJoinDataDto(data);
        processData(data);
      }
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const processData = (data: IDbJoinDataDto[]) => {
    if (!data || data.length === 0) return;

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    const originTable = data.find((x) => x.dependencyTypeByCurrentTable === "Origin");
    if (!originTable) return;

    // Create origin node
    const originNodeId = `origin-${originTable.nodeTable}`;
    const originSourceHandles: FkPkHandle[] = [];
    const originTargetHandles: FkPkHandle[] = [];

    // Get all parent and child relationships for origin
    const parentData = data.filter((x) => x.dependencyTypeByCurrentTable === "Parent");
    const childData = data.filter((x) => x.dependencyTypeByCurrentTable === "Child");

    // Origin's source handles (FKs pointing to parents)
    parentData
      .filter((x) => x.childNodeId === originTable.nodeId)
      .forEach((parent) => {
        originSourceHandles.push({
          id: `${originNodeId}-source-${parent.fk}`,
          type: "source",
          columnName: parent.fk
        });
      });

    // Origin's target handles (PKs that children point to)
    // Use child.fk in the ID to make handles unique per relationship
    childData
      .filter((x) => x.parentNodeId === originTable.nodeId)
      .forEach((child) => {
        originTargetHandles.push({
          id: `${originNodeId}-target-${child.fk}`,
          type: "target",
          columnName: child.pk
        });
      });

    nodes.push({
      id: originNodeId,
      targetPosition: rotation === "vertical" ? Position.Top : Position.Left,
      sourcePosition: rotation === "vertical" ? Position.Bottom : Position.Right,
      position: { x: -nodeWidth / 2, y: -nodeHeight / 2 },
      selectable: true,
      data: {
        label: originTable.nodeTable,
        tableId: originTable.nodeTableId,
        tableName: originTable.nodeTable,
        tableSchema: originTable.nodeTableSchema,
        type: ETreeType.Origin,
        columns: getTableColumns(originTable.nodeTable),
        sourceHandles: originSourceHandles,
        targetHandles: originTargetHandles
      },
      style: {
        width: nodeWidth,
        height: nodeHeight,
        backgroundColor: selectedColor
      },
      type: ETreeType.Origin
    });

    // Process parents - create one node per FK relationship
    processParentNodes(originNodeId, originTable, parentData, data, nodes, edges, 1);

    // Process children - create one node per FK relationship
    processChildNodes(originNodeId, originTable, childData, data, nodes, edges, 1);

    setNodes(nodes);
    setEdges(edges);
  };

  const processParentNodes = (
    currentNodeId: string,
    currentData: IDbJoinDataDto,
    parentData: IDbJoinDataDto[],
    allData: IDbJoinDataDto[],
    nodes: Node[],
    edges: Edge[],
    level: number
  ) => {
    // Get all parents of current node
    const directParents = parentData.filter((x) => x.childNodeId === currentData.nodeId);

    directParents.forEach((parent, index) => {
      // Create unique node ID for this FK relationship
      const nodeId = `parent-${parent.nodeTable}-${parent.fk}-${level}-${index}`;

      // This node's handles
      const sourceHandles: FkPkHandle[] = [];
      const targetHandles: FkPkHandle[] = [];

      // Target handle for the PK that receives connection from child
      targetHandles.push({
        id: `${nodeId}-target-${parent.pk}`,
        type: "target",
        columnName: parent.pk
      });

      // Check if this parent has its own parents (grandparents)
      const grandparents = parentData.filter((x) => x.childNodeId === parent.nodeId);
      grandparents.forEach((gp) => {
        sourceHandles.push({
          id: `${nodeId}-source-${gp.fk}`,
          type: "source",
          columnName: gp.fk
        });
      });

      // Calculate position
      const position = calculateNodePosition("parents", level, index, directParents.length);

      const node: Node = {
        id: nodeId,
        targetPosition: rotation === "vertical" ? Position.Top : Position.Left,
        sourcePosition: rotation === "vertical" ? Position.Bottom : Position.Right,
        position,
        selectable: true,
        data: {
          label: `${parent.nodeTable} [${parent.fk}]`,
          tableId: parent.nodeTableId,
          tableName: parent.nodeTable,
          tableSchema: parent.nodeTableSchema,
          type: ETreeType.Parent,
          columns: getTableColumns(parent.nodeTable),
          fk: parent.fk,
          pk: parent.pk,
          parentNodeId: currentNodeId,
          masterTable: parent.masterTable,
          masterTableSchema: parent.masterTableSchema,
          masterTableId: parent.masterTableId,
          detailTable: parent.detailTable,
          detailTableSchema: parent.detailTableSchema,
          detailTableId: parent.detailTableId,
          sourceHandles,
          targetHandles,
          level
        },
        style: {
          width: nodeWidth,
          height: nodeHeight,
          backgroundColor: parentColor
        },
        type: ETreeType.Parent
      };

      nodes.push(node);

      // Create edge from current node to this parent
      const edge: Edge = {
        id: `edge-${currentNodeId}-${nodeId}-${parent.fk}`,
        source: currentNodeId,
        sourceHandle: `${currentNodeId}-source-${parent.fk}`,
        target: nodeId,
        targetHandle: `${nodeId}-target-${parent.pk}`,
        animated: false,
        type: "smoothstep",
        style: { strokeWidth: 2, pointerEvents: "none" as const },
        focusable: false,
        data: {
          type: ETreeType.Parent,
          fk: parent.fk,
          pk: parent.pk
        }
      };

      edges.push(edge);

      // Recursively process grandparents
      processParentNodes(nodeId, parent, parentData, allData, nodes, edges, level + 1);
    });
  };

  const processChildNodes = (
    currentNodeId: string,
    currentData: IDbJoinDataDto,
    childData: IDbJoinDataDto[],
    allData: IDbJoinDataDto[],
    nodes: Node[],
    edges: Edge[],
    level: number
  ) => {
    // Get all children of current node
    const directChildren = childData.filter((x) => x.parentNodeId === currentData.nodeId);

    directChildren.forEach((child, index) => {
      // Create unique node ID for this FK relationship
      const nodeId = `child-${child.nodeTable}-${child.fk}-${level}-${index}`;

      // This node's handles
      const sourceHandles: FkPkHandle[] = [];
      const targetHandles: FkPkHandle[] = [];

      // Source handle for the FK that connects to parent
      sourceHandles.push({
        id: `${nodeId}-source-${child.fk}`,
        type: "source",
        columnName: child.fk
      });

      // Check if this child has its own children (grandchildren)
      // Use gc.fk in the ID to make handles unique per relationship
      const grandchildren = childData.filter((x) => x.parentNodeId === child.nodeId);
      grandchildren.forEach((gc) => {
        targetHandles.push({
          id: `${nodeId}-target-${gc.fk}`,
          type: "target",
          columnName: gc.pk
        });
      });

      // Calculate position
      const position = calculateNodePosition("children", level, index, directChildren.length);

      const node: Node = {
        id: nodeId,
        targetPosition: rotation === "vertical" ? Position.Top : Position.Left,
        sourcePosition: rotation === "vertical" ? Position.Bottom : Position.Right,
        position,
        selectable: true,
        data: {
          label: `${child.nodeTable} [${child.fk}]`,
          tableId: child.nodeTableId,
          tableName: child.nodeTable,
          tableSchema: child.nodeTableSchema,
          type: ETreeType.Child,
          columns: getTableColumns(child.nodeTable),
          fk: child.fk,
          pk: child.pk,
          parentNodeId: currentNodeId,
          masterTable: child.masterTable,
          masterTableSchema: child.masterTableSchema,
          masterTableId: child.masterTableId,
          detailTable: child.detailTable,
          detailTableSchema: child.detailTableSchema,
          detailTableId: child.detailTableId,
          sourceHandles,
          targetHandles,
          level
        },
        style: {
          width: nodeWidth,
          height: nodeHeight,
          backgroundColor: childColor
        },
        type: ETreeType.Child
      };

      nodes.push(node);

      // Create edge from this child to current node (parent)
      // Use child.fk for targetHandle to match the unique handle ID
      const edge: Edge = {
        id: `edge-${nodeId}-${currentNodeId}-${child.fk}`,
        source: nodeId,
        sourceHandle: `${nodeId}-source-${child.fk}`,
        target: currentNodeId,
        targetHandle: `${currentNodeId}-target-${child.fk}`,
        animated: false,
        type: "smoothstep",
        style: { strokeWidth: 2, pointerEvents: "none" as const },
        focusable: false,
        data: {
          type: ETreeType.Child,
          fk: child.fk,
          pk: child.pk
        }
      };

      edges.push(edge);

      // Recursively process grandchildren
      processChildNodes(nodeId, child, childData, allData, nodes, edges, level + 1);
    });
  };

  const calculateNodePosition = (
    direction: "parents" | "children",
    level: number,
    indexInLevel: number,
    countInLevel: number
  ) => {
    let x: number;
    let y: number;

    if (rotation === "vertical") {
      const totalWidth = countInLevel * nodeWidth + (countInLevel - 1) * nodeWidthGap;
      const startX = -totalWidth / 2;
      x = startX + indexInLevel * (nodeWidth + nodeWidthGap);

      if (direction === "parents") {
        y = -nodeHeight / 2 - level * (nodeHeight + nodeHeightGap);
      } else {
        y = -nodeHeight / 2 + level * (nodeHeight + nodeHeightGap);
      }
    } else {
      const totalHeight = countInLevel * nodeHeight + (countInLevel - 1) * nodeHeightGap;
      const startY = -totalHeight / 2;
      y = startY + indexInLevel * (nodeHeight + nodeHeightGap);

      if (direction === "parents") {
        x = -nodeWidth / 2 - level * (nodeWidth + nodeWidthGap);
      } else {
        x = -nodeWidth / 2 + level * (nodeWidth + nodeWidthGap);
      }
    }

    return { x, y };
  };

  const getTableColumns = (tableName: string): IDatabaseColumnModel[] => {
    const table = props.tables.find((x) => x.name === tableName);
    if (!table) return [];
    return table.columns;
  };

  // Find path between two nodes
  const findPath = (startNodeId: string, endNodeId: string): string[] | null => {
    // BFS to find path
    const queue: { nodeId: string; path: string[] }[] = [{ nodeId: startNodeId, path: [startNodeId] }];
    const visited = new Set<string>();

    while (queue.length > 0) {
      const { nodeId, path } = queue.shift()!;

      if (nodeId === endNodeId) {
        return path;
      }

      if (visited.has(nodeId)) continue;
      visited.add(nodeId);

      // Find connected nodes through edges
      edges.forEach((edge) => {
        let nextNodeId: string | null = null;
        if (edge.source === nodeId && !visited.has(edge.target)) {
          nextNodeId = edge.target;
        } else if (edge.target === nodeId && !visited.has(edge.source)) {
          nextNodeId = edge.source;
        }

        if (nextNodeId) {
          queue.push({ nodeId: nextNodeId, path: [...path, nextNodeId] });
        }
      });
    }

    return null; // No path found
  };

  // Get edges for a path
  const getEdgesForPath = (nodePath: string[]): string[] => {
    const edgeIds: string[] = [];
    for (let i = 0; i < nodePath.length - 1; i++) {
      const currentNode = nodePath[i];
      const nextNode = nodePath[i + 1];

      const edge = edges.find(
        (e) =>
          (e.source === currentNode && e.target === nextNode) || (e.source === nextNode && e.target === currentNode)
      );

      if (edge) {
        edgeIds.push(edge.id);
      }
    }
    return edgeIds;
  };

  // Check if two nodes are directly connected via an edge
  const areNodesConnected = (nodeId1: string, nodeId2: string): boolean => {
    return edges.some(
      (edge) =>
        (edge.source === nodeId1 && edge.target === nodeId2) || (edge.source === nodeId2 && edge.target === nodeId1)
    );
  };

  // Get the edge between two connected nodes
  const getEdgeBetweenNodes = (nodeId1: string, nodeId2: string): Edge | undefined => {
    return edges.find(
      (edge) =>
        (edge.source === nodeId1 && edge.target === nodeId2) || (edge.source === nodeId2 && edge.target === nodeId1)
    );
  };

  const handleNodeClick = (event: React.MouseEvent, clickedNode: Node) => {
    const { selectedPath } = pathSelection;

    if (selectedPath.length === 0) {
      // First click - start new path
      setPathSelection({ selectedPath: [clickedNode] });
      highlightNode(clickedNode.id);
    } else {
      const lastNode = selectedPath[selectedPath.length - 1];

      // Check if clicked the same node as the last one - remove it from path
      if (clickedNode.id === lastNode.id) {
        if (selectedPath.length === 1) {
          // Only one node, clear selection
          setPathSelection({ selectedPath: [] });
          clearHighlights();
        } else {
          // Remove last node from path
          const newPath = selectedPath.slice(0, -1);
          setPathSelection({ selectedPath: newPath });
          const nodeIds = newPath.map((n) => n.id);
          const edgeIds = getEdgesForPath(nodeIds);
          highlightPath(nodeIds, edgeIds);
        }
        return;
      }

      // Check if the node is already in the path (but not the last one)
      const existingIndex = selectedPath.findIndex((n) => n.id === clickedNode.id);
      if (existingIndex !== -1) {
        // Truncate path to this node (go back to this point)
        const newPath = selectedPath.slice(0, existingIndex + 1);
        setPathSelection({ selectedPath: newPath });
        const nodeIds = newPath.map((n) => n.id);
        const edgeIds = getEdgesForPath(nodeIds);
        highlightPath(nodeIds, edgeIds);
        return;
      }

      // Check if clicked node is directly connected to the last node in path
      if (areNodesConnected(lastNode.id, clickedNode.id)) {
        // Add to existing path
        const newPath = [...selectedPath, clickedNode];
        setPathSelection({ selectedPath: newPath });
        const nodeIds = newPath.map((n) => n.id);
        const edgeIds = getEdgesForPath(nodeIds);
        highlightPath(nodeIds, edgeIds);
      } else {
        // Not connected - start a new path from this node
        setPathSelection({ selectedPath: [clickedNode] });
        highlightNode(clickedNode.id);
      }
    }
  };

  const highlightNode = (nodeId: string) => {
    setNodes((prevNodes) =>
      prevNodes.map((node) => ({
        ...node,
        selected: node.id === nodeId,
        className: node.id === nodeId ? "path-selected" : ""
      }))
    );

    setEdges((prevEdges) =>
      prevEdges.map((edge) => ({
        ...edge,
        selected: false,
        className: ""
      }))
    );
  };

  const highlightPath = (nodeIds: string[], edgeIds: string[]) => {
    setNodes((prevNodes) =>
      prevNodes.map((node) => ({
        ...node,
        selected: nodeIds.includes(node.id),
        className: nodeIds.includes(node.id) ? "path-selected" : ""
      }))
    );

    setEdges((prevEdges) =>
      prevEdges.map((edge) => ({
        ...edge,
        selected: edgeIds.includes(edge.id),
        className: edgeIds.includes(edge.id) ? "path-selected" : ""
      }))
    );
  };

  const highlightNodes = (nodeIds: string[], edgeIds: string[]) => {
    setNodes((prevNodes) =>
      prevNodes.map((node) => ({
        ...node,
        selected: nodeIds.includes(node.id),
        className: nodeIds.includes(node.id) ? "path-selected" : ""
      }))
    );

    setEdges((prevEdges) =>
      prevEdges.map((edge) => ({
        ...edge,
        selected: edgeIds.includes(edge.id),
        className: edgeIds.includes(edge.id) ? "path-selected" : ""
      }))
    );
  };

  const clearHighlights = () => {
    setNodes((prevNodes) =>
      prevNodes.map((node) => ({
        ...node,
        selected: false,
        className: ""
      }))
    );

    setEdges((prevEdges) =>
      prevEdges.map((edge) => ({
        ...edge,
        selected: false,
        className: ""
      }))
    );
  };

  const saveJoin = () => {
    const { selectedPath } = pathSelection;

    if (selectedPath.length === 0) return;

    const joinsToAdd: IGenericSelectDataModel = {
      tables: [],
      fields: [],
      calculatedFields: [],
      relations: [],
      whereData: [],
      sortData: []
    };

    // Check if origin is in the path
    const originNode = nodes.find((n) => n.data.type === ETreeType.Origin);
    const originInPath = selectedPath.some((n) => n.id === originNode?.id);

    if (selectedPath.length === 1) {
      // Single node selected - add path from that node to origin
      const startNode = selectedPath[0];
      if (startNode.id === originNode?.id) return;

      const path = findPath(startNode.id, originNode!.id);
      if (!path) return;

      addPathJoins(path, joinsToAdd);
    } else if (originInPath) {
      // Origin is in the selected path - use the selected path directly
      const pathNodeIds = selectedPath.map((n) => n.id);
      addPathJoins(pathNodeIds, joinsToAdd);
    } else {
      // Multiple nodes selected without origin - add path from first node to origin, then follow selected path
      const firstNode = selectedPath[0];
      const pathToOrigin = findPath(firstNode.id, originNode!.id);

      if (pathToOrigin) {
        // Combine: path to origin + rest of selected path (skip first node to avoid duplicate)
        const fullPath = [...pathToOrigin];
        for (let i = 1; i < selectedPath.length; i++) {
          if (!fullPath.includes(selectedPath[i].id)) {
            fullPath.push(selectedPath[i].id);
          }
        }
        addPathJoins(fullPath, joinsToAdd);
      } else {
        // Just use selected path
        const pathNodeIds = selectedPath.map((n) => n.id);
        addPathJoins(pathNodeIds, joinsToAdd);
      }
    }

    // Determine tree type based on nodes in path
    const treeType = determineTreeType(joinsToAdd);
    props.handleAddJoins(joinsToAdd, treeType);
  };

  const addPathJoins = (path: string[], joinsToAdd: IGenericSelectDataModel) => {
    // Track which tables have been added (by tableName) to avoid duplicates
    const addedTables = new Set<string>();

    // Process each node in the path and add tables with their INCOMING FK
    // (the FK used to reach this table from the previous node in path)
    for (let i = 0; i < path.length; i++) {
      const nodeId = path[i];
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) continue;

      // Skip origin node (it's already in the tables list)
      if (node.data.type === ETreeType.Origin) continue;

      const tableName = node.data.tableName;

      // Skip if this table is already added
      if (addedTables.has(tableName)) continue;

      // Find the incoming edge (edge from previous node in path to this node)
      // This determines the FK used to reach this table
      let incomingFk: string | undefined;
      if (i > 0) {
        const prevNodeId = path[i - 1];
        const incomingEdge = edges.find(
          (e) => (e.source === prevNodeId && e.target === nodeId) || (e.source === nodeId && e.target === prevNodeId)
        );
        if (incomingEdge) {
          incomingFk = incomingEdge.data.fk;
        }
      }

      joinsToAdd.tables.push({
        id: node.data.tableId,
        name: tableName,
        schema: node.data.tableSchema,
        joinedViaFk: incomingFk
      });
      addedTables.add(tableName);
    }

    // Add relations for all edges in path
    for (let i = 0; i < path.length - 1; i++) {
      const edge = edges.find(
        (e) => (e.source === path[i] && e.target === path[i + 1]) || (e.source === path[i + 1] && e.target === path[i])
      );
      if (!edge) continue;

      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);
      if (!sourceNode || !targetNode) continue;

      const edgeData = edge.data;

      // Check if relation already exists
      const existingRelation = joinsToAdd.relations.find(
        (r) =>
          r.fromFk === edgeData.fk &&
          r.fromTable === sourceNode.data.tableName &&
          r.toTable === targetNode.data.tableName
      );

      if (!existingRelation) {
        joinsToAdd.relations.push({
          fromFk: edgeData.fk,
          fromTable: sourceNode.data.tableName,
          fromTableSchema: sourceNode.data.tableSchema,
          fromTableId: sourceNode.data.tableId,
          toPk: edgeData.pk,
          toTable: targetNode.data.tableName,
          toTableSchema: targetNode.data.tableSchema,
          toTableId: targetNode.data.tableId,
          joinType: EJoinType.leftJoin
        });
      }
    }
  };

  const determineTreeType = (joinsToAdd: IGenericSelectDataModel): ETreeType => {
    // Check if any of the added tables are parent or child type
    const hasParent = joinsToAdd.tables.some((t) => {
      const node = nodes.find((n) => n.data.tableName === t.name && n.data.fk === t.joinedViaFk);
      return node?.data.type === ETreeType.Parent;
    });

    return hasParent ? ETreeType.Parent : ETreeType.Child;
  };

  const onNodesChange = (changes: NodeChange[]) => {
    const positionChanges = changes.filter((change) => change.type === "position" || change.type === "dimensions");
    if (positionChanges.length > 0) {
      setNodes((nds) => applyNodeChanges(positionChanges, nds));
    }
  };

  const canSave =
    pathSelection.selectedPath.length > 0 && pathSelection.selectedPath.some((n) => n.data.type !== ETreeType.Origin);

  // The warning panel takes the join's place, so the commit becomes the way out of it.
  const renderFooter = () => (
    <KModalFooter
      submitLabel={warningPanel.isOpen ? "Cancel" : "Save join"}
      onSubmit={warningPanel.isOpen ? props.closeModal : saveJoin}
      disabled={!warningPanel.isOpen && !canSave}
    />
  );

  return (
    <KModal
      className="kuika__sql-action-add-join-modal"
      showModal={true}
      centered
      bodyStyle={{ height: isMaximized ? "calc(100vh - 118px)" : "70vh" }}
      width={isMaximized ? "calc(100vw)" : "90vw"}
      header={
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "space-between", width: "100%" }}
        >
          <KuikaSmallBoldText>Select Join</KuikaSmallBoldText>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <KuikaButton
              isIcon
              type="secondary"
              size="sm"
              onClick={() => setIsMaximized(!isMaximized)}
              active={isMaximized}
            >
              <KuikaSvg name="maximize" width={16} height={16} />
            </KuikaButton>
            <KuikaButton color="neutral" type="tertiary" isIcon size="md" onClick={props.closeModal}>
              <KuikaSvg name="x" width={18} height={18} />
            </KuikaButton>
          </div>
        </div>
      }
      hideCloseIcon={true}
      closeModal={props.closeModal}
      footer={renderFooter()}
    >
      <div className="add-join-modal__content">
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%"
            }}
          >
            <KuikaSpinnerSmall />
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            fitView
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            zoomOnScroll={true}
            onNodesChange={onNodesChange}
            onNodeClick={handleNodeClick}
            onPaneClick={() => {
              setPathSelection({ selectedPath: [] });
              clearHighlights();
            }}
            onInit={(instance) => setReactflowInstance(instance)}
            nodeTypes={nodeTypes}
            style={{ backgroundColor: "var(--dark-1)" }}
          >
            <Panel position="bottom-left">
              <ZoomControls joinDataDto={joinDataDto} rotation={rotation} setRotation={setRotation} />
            </Panel>
            <Panel position="top-right">
              <div className="add-join-modal__color-infobox-wrapper">
                <div className="add-join-modal__color-infobox">
                  <div className="add-join-modal__color-box add-join-modal__color-box--parent"></div>
                  <KuikaText>Parent</KuikaText>
                </div>
                <div className="add-join-modal__color-infobox">
                  <div className="add-join-modal__color-box add-join-modal__color-box--origin"></div>
                  <KuikaText>Origin</KuikaText>
                </div>
                <div className="add-join-modal__color-infobox">
                  <div className="add-join-modal__color-box add-join-modal__color-box--child"></div>
                  <KuikaText>Child</KuikaText>
                </div>
              </div>
            </Panel>
            <Panel position="top-center">
              <div
                className={`add-join-modal__instruction-panel ${
                  pathSelection.selectedPath.length > 0 ? "add-join-modal__instruction-panel--active" : ""
                }`}
              >
                <KuikaSvg
                  name={pathSelection.selectedPath.length > 0 ? "circle-check" : "hand-click"}
                  width={16}
                  height={16}
                  className="add-join-modal__instruction-icon"
                />
                <span className="add-join-modal__instruction-text">
                  {pathSelection.selectedPath.length === 0
                    ? "Click a node to start selection"
                    : pathSelection.selectedPath.length === 1
                      ? "Click connected nodes to build path, or click Save"
                      : `${pathSelection.selectedPath.length} nodes selected — Continue building or Save`}
                </span>
                {pathSelection.selectedPath.length > 0 && (
                  <KuikaButton
                    type="tertiary"
                    color="neutral"
                    size="sm"
                    className="add-join-modal__instruction-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPathSelection({ selectedPath: [] });
                      clearHighlights();
                    }}
                  >
                    <KuikaSvg name="x" width={14} height={14} />
                  </KuikaButton>
                )}
              </div>
            </Panel>
            {warningPanel.isOpen && (
              <Panel position="top-left">
                <div className="add-join-modal__warning-panel">
                  <KuikaText>{warningPanel.message}</KuikaText>
                </div>
              </Panel>
            )}
          </ReactFlow>
        )}
      </div>
    </KModal>
  );
};
export const AddJoinModalComponent = AddJoinModal;
